import React, { useRef, useState } from 'react';

import { type Shown, delta, formatExact, formatValue, memberLabel } from '../format';
import { seriesOverrides } from '../seriesStyle';
import { columnGroups, linkFor, toneFor } from '../tableExtras';
import type { Block, Envelope, EnvelopeColumn, EnvelopeRow } from '../types';

const PRINT_SLOTS = 7;
const SHOWN_ROWS = 12;
const ROWS_PER_PAGE = 25;
const DELTA_SLOT = 0.5;
const LABEL_SLOT = 1;

type ColumnWidths = Record<string, number>;
type DetailTableProps = { block?: Block; envelope: Envelope; totalLabel?: string; shown: Shown; picked?: string; selected?: Array<string>; onPick?: (member: string) => void; onColumnWidthsChange?: (widths: ColumnWidths) => void };
const MIN_COLUMN_WIDTH = 80;
const MAX_COLUMN_WIDTH = 2000;
const columnWidth = (width: number) => Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(width)));

export function comparisonOnlyRows(current: EnvelopeRow[], compared: EnvelopeRow[], dimensions: string[], measures: string[]): EnvelopeRow[] {
  const keyOf = (row: EnvelopeRow) => dimensions.map((name) => row[name] === undefined ? '' : String(row[name])).join('›');
  const currentKeys = new Set(current.map(keyOf));
  const measureNames = new Set(measures);
  return compared
    .filter((row) => !currentKeys.has(keyOf(row)))
    .map((row) => Object.fromEntries(Object.entries(row).filter(([name]) => !measureNames.has(name))));
}

export function detailChildLabel(row: EnvelopeRow, dimensions: string[]): string {
  return dimensions.slice(1).map((name) => memberLabel(String(row[name] ?? ''))).join(' · ');
}

function compareCells(a: EnvelopeRow[string] | undefined, b: EnvelopeRow[string] | undefined): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a === null || a === undefined) return b === null || b === undefined ? 0 : -1;
  if (b === null || b === undefined) return 1;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}


export default function DetailTable({ block, envelope, totalLabel = 'All', shown, picked, selected = [], onPick, onColumnWidthsChange }: DetailTableProps) {
  const overrides = seriesOverrides(block);
  const dims = envelope.columns.filter((column) => column.role === 'dim' || column.role === 'time').map((column) => ({ ...column, label: overrides[column.name]?.label ?? column.label }));
  const measures = envelope.columns.filter((column) => column.role === 'measure').map((column) => ({ ...column, label: overrides[column.name]?.label ?? column.label }));
  const comparison = envelope.period.comparisons[0];
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [paged, setPaged] = useState(false);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ column: string; descending: boolean } | undefined>(block?.sortBy as { column: string; descending: boolean } | undefined);
  const savedWidths = block?.columnWidths as ColumnWidths | undefined;
  const [dragWidths, setDragWidths] = useState<ColumnWidths | undefined>();
  const resizing = useRef<{ key: string; startX: number; startWidth: number; widths: ColumnWidths } | null>(null);
  const pendingKeyboardWidths = useRef<ColumnWidths | null>(null);
  const widths = dragWidths ?? savedWidths;
  const flushKeyboardResize = () => {
    const next = pendingKeyboardWidths.current;
    if (!next) return;
    pendingKeyboardWidths.current = null;
    setDragWidths(undefined);
    onColumnWidthsChange?.(next);
  };
  const startResize = (event: React.PointerEvent<HTMLSpanElement>, key: string) => {
    event.preventDefault();
    event.stopPropagation();
    flushKeyboardResize();
    const table = event.currentTarget.closest('table');
    if (!table) return;
    const measured = Object.fromEntries(Array.from(table.querySelectorAll<HTMLTableCellElement>('thead tr:last-child th[data-column-key]')).map((cell) => [cell.dataset.columnKey!, columnWidth(cell.getBoundingClientRect().width)]));
    resizing.current = { key, startX: event.clientX, startWidth: measured[key], widths: measured };
    setDragWidths(measured);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const moveResize = (event: React.PointerEvent<HTMLSpanElement>) => {
    if (!resizing.current) return;
    const { key, startX, startWidth, widths: initial } = resizing.current;
    setDragWidths({ ...initial, [key]: columnWidth(startWidth + event.clientX - startX) });
  };
  const finishResize = (event: React.PointerEvent<HTMLSpanElement>) => {
    if (!resizing.current) return;
    const { key, startX, startWidth, widths: initial } = resizing.current;
    const next = { ...initial, [key]: columnWidth(startWidth + event.clientX - startX) };
    resizing.current = null;
    setDragWidths(undefined);
    if (next[key] !== startWidth) onColumnWidthsChange?.(next);
  };
  const resizeHandle = (key: string, label: string) => onColumnWidthsChange && !printing ? (
    <span
      aria-label={`Resize ${label} column`}
      aria-orientation="vertical"
      className="column-resize"
      onBlur={flushKeyboardResize}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        event.stopPropagation();
        const table = event.currentTarget.closest('table');
        if (!table) return;
        const measured = pendingKeyboardWidths.current ?? Object.fromEntries(Array.from(table.querySelectorAll<HTMLTableCellElement>('thead tr:last-child th[data-column-key]')).map((cell) => [cell.dataset.columnKey!, columnWidth(cell.getBoundingClientRect().width)]));
        const next = { ...measured, [key]: columnWidth(measured[key] + (event.key === 'ArrowRight' ? 10 : -10)) };
        if (next[key] === measured[key]) return;
        pendingKeyboardWidths.current = next;
        setDragWidths(next);
      }}
      onKeyUp={(event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        event.stopPropagation();
        flushKeyboardResize();
      }}
      onPointerCancel={() => { resizing.current = null; setDragWidths(undefined); }}
      onPointerDown={(event) => startResize(event, key)}
      onPointerMove={moveResize}
      onPointerUp={finishResize}
      role="separator"
      tabIndex={0}
    />
  ) : null;
  const markMore = (node: HTMLDivElement | null) => {
    node?.toggleAttribute('data-more', node.scrollLeft + node.clientWidth < node.scrollWidth - 1);
  };
  const nested = !envelope.raw && dims.length > 1 && envelope.rows.some((row) => row.level === 1);
  const parents = envelope.rows.filter((row) => row.level === 1);
  const childrenOf = (parent: EnvelopeRow) => envelope.rows.filter((row) => row.level === dims.length && row[dims[0].name] === parent[dims[0].name]);
  const tree = nested && parents.some((parent) => childrenOf(parent).length > 1);
  const shownDims = tree ? dims.slice(0, 1) : dims;
  const keyOf = (row: EnvelopeRow) => dims.map((dim) => (row[dim.name] === undefined ? '' : String(row[dim.name]))).join('›');
  const before = new Map((comparison ? envelope.compare[comparison.id] ?? [] : []).map((row) => [keyOf(row), row]));
  const declared = block?.columns as Array<{ field: string; kind: string }> | undefined;
  const changeColumns = comparison ? (shown.delta === 'both' ? (['diff', 'pct'] as const) : shown.delta === 'none' ? [] : [shown.delta]) : [];
  const extrasOf = (measure: EnvelopeColumn) => comparison ? declared ? declared.filter((column) => column.field === measure.name && ['prior', 'change', 'change_pct'].includes(column.kind)).map((column) => column.kind === 'change' ? 'diff' : column.kind === 'change_pct' ? 'pct' : 'prior') : [...(shown.baseline ? ['prior'] : []), ...changeColumns] : [];
  const signed = (change: ReturnType<typeof delta>) => (change ? `${change.arrow === '▼' ? '−' : change.arrow === '▲' && !/^(new|>)/.test(change.text) ? '+' : ''}${change.text}` : '');
  const printing = typeof document !== 'undefined' && Boolean(document.getElementById('lens-print'));
  const perSlice = Math.max(1, Math.floor((PRINT_SLOTS - LABEL_SLOT * Math.max(0, shownDims.length - 1)) / (1 + DELTA_SLOT * changeColumns.length)));
  const slices: Array<Array<EnvelopeColumn>> = printing && measures.length > perSlice ? Array.from({ length: Math.ceil(measures.length / perSlice) }, (_, index) => measures.slice(index * perSlice, (index + 1) * perSlice)) : [measures];
  const cells = (slice: Array<EnvelopeColumn>, row: EnvelopeRow | undefined, base: EnvelopeRow | undefined, key: string) =>
    slice.flatMap((measure) => {
      const value = row?.[measure.name] as number | null | undefined;
      const prior = base?.[measure.name] as number | null | undefined;
      const sample = row?.[`n:${measure.name}`] as number | null | undefined;
      const thin = measure.minN !== undefined && sample !== null && sample !== undefined && sample < measure.minN;
      return [
        <td key={measure.name}>
          <span className={`sv${thin ? ' thin' : ''}`} data-lens-ref={`${envelope.query}.${measure.name}[${key}]`} title={formatExact(value, measure.format)}>
            {key === 'total' || !toneFor(block, measure.name, value) ? (
              formatValue(value, measure.format, { numbers: shown.numbers, decimals: measure.decimals })
            ) : (
              <span className={`pill ${toneFor(block, measure.name, value)}`}>{formatValue(value, measure.format, { numbers: shown.numbers, decimals: measure.decimals })}</span>
            )}
            {thin ? <span className="n">n={sample ?? 0}</span> : null}
          </span>
        </td>,
        ...extrasOf(measure).map((style) => {
          const change = thin ? undefined : style === 'prior' ? undefined : style === 'pct' ? delta(value, prior, { ...measure, format: 'num1', decimals: 1 }, 'pct', shown.numbers) : delta(value, prior, measure, 'diff', shown.numbers);
          return (
            <td className={`sd ${change?.tone ?? ''}`} data-lens-ref={`${envelope.query}.${measure.name}[${key}]@${comparison!.id}`} key={`${measure.name}:${style}`}>
              {style === 'prior' ? formatValue(prior, measure.format, { numbers: shown.numbers, decimals: measure.decimals }) : style === 'pct' ? !thin && value != null && prior != null && prior !== 0 ? `${value - prior >= 0 ? '+' : ''}${formatValue((value - prior) / Math.abs(prior), 'pct', { decimals: 1 })}` : '–' : signed(change)}
            </td>
          );
        }),
      ];
    });
  const member = (row: EnvelopeRow, dim: EnvelopeColumn) => {
    const label = envelope.raw ? String(row[dim.name] ?? '–') : memberLabel(String(row[dim.name] ?? ''));
    const tone = toneFor(block, dim.name, row[dim.name]);
    const shownLabel = tone ? <span className={`pill ${tone}`}>{label}</span> : label;
    const href = linkFor(block, dim.name, row);
    return href ? (
      <a className="lk" href={href} rel="noopener noreferrer" target="_blank">
        {shownLabel}
      </a>
    ) : (
      shownLabel
    );
  };
  const rowProps = (row: EnvelopeRow) => {
    if (envelope.raw) return {};
    const value = dims[0] ? String(row[dims[0].name] ?? '') : '';
    if (row.other || !value) return {};
    if (!onPick) return { 'data-lens-part': `member:${value}` };
    const state = selected.includes(value) ? 'on' : selected.length ? 'dim' : '';
    return {
      'data-lens-part': `member:${value}`,
      'aria-pressed': state === 'on',
      className: ['pick', state].filter(Boolean).join(' '),
      onClick: (event: React.MouseEvent) => !(event.target as HTMLElement).closest('a, button') && onPick(value),
      onKeyDown: (event: React.KeyboardEvent) => {
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onPick(value);
        }
      },
      tabIndex: 0,
      title: state === 'on' ? 'Click to clear this filter' : 'Click to filter the board to this row',
    };
  };
  const priorOnly = comparison ? comparisonOnlyRows(envelope.rows, envelope.compare[comparison.id] ?? [], dims.map((dim) => dim.name), measures.map((measure) => measure.name)) : [];
  const rows = [...envelope.rows, ...priorOnly];
  const unsorted = tree ? parents : nested ? rows.filter((row) => row.level === dims.length) : rows;
  const topLevel = sort ? [...unsorted].sort((a, b) => Number(Boolean(a.other)) - Number(Boolean(b.other)) || compareCells(a[sort.column], b[sort.column]) * (sort.descending ? -1 : 1)) : unsorted;
  const lastPage = Math.max(0, Math.ceil(topLevel.length / ROWS_PER_PAGE) - 1);
  const currentPage = Math.min(page, lastPage);
  const visibleRows = paged && !printing ? topLevel.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE) : topLevel.slice(0, SHOWN_ROWS);
  const heading = (column: EnvelopeColumn, label: React.ReactNode = column.label) => {
    const current = sort?.column === column.name ? sort : undefined;
    const first = column.role === 'measure';
    const next = !current
      ? { column: column.name, descending: first }
      : current.descending === first
        ? { column: column.name, descending: !first }
        : undefined;
    return printing ? (
      label
    ) : (
      <button aria-label={`Sort by ${column.label}${current ? (current.descending ? ', highest first' : ', lowest first') : ''}`} className="sort" onClick={() => { setPage(0); setSort(next); }} type="button">
        {label}
        {current ? <span aria-hidden>{current.descending ? ' ↓' : ' ↑'}</span> : null}
      </button>
    );
  };
  const sortedBy = (column: EnvelopeColumn) => (sort?.column === column.name ? (sort.descending ? 'descending' : 'ascending') : undefined);
  const columnKeys = (slice: Array<EnvelopeColumn>) => [
    ...(shownDims.length ? shownDims.map((dim) => `dim:${dim.name}`) : ['total']),
    ...slice.flatMap((measure) => [`measure:${measure.name}`, ...extrasOf(measure).map((style) => `extra:${measure.name}:${style}`)]),
  ];
  const table = (slice: Array<EnvelopeColumn>, index: number) => (
    <div className="tbl" key={index} onScroll={(event) => markMore(event.currentTarget)} ref={markMore}>
      {slices.length > 1 ? <p className="bn">{`Columns ${index * perSlice + 1}–${index * perSlice + slice.length} of ${measures.length}`}</p> : null}
      <table style={widths && columnKeys(slice).every((key) => widths[key]) ? { tableLayout: 'fixed', width: columnKeys(slice).reduce((total, key) => total + widths[key], 0) } : undefined}>
        {widths && columnKeys(slice).every((key) => widths[key]) ? <colgroup>{columnKeys(slice).map((key) => <col key={key} style={{ width: widths[key] }} />)}</colgroup> : null}
        <thead>
          {columnGroups(block).length ? (
            <tr className="groups">
              <th colSpan={Math.max(1, shownDims.length)} />
              {slice.reduce<Array<{ label: string; span: number }>>((spans, measure) => {
                const label = columnGroups(block).find((group) => group.columns.includes(measure.name))?.label ?? '';
                const width = 1 + extrasOf(measure).length;
                const last = spans[spans.length - 1];
                if (last && last.label === label) last.span += width;
                else spans.push({ label, span: width });
                return spans;
              }, []).map((group, index) => (
                <th className={group.label ? 'group' : undefined} colSpan={group.span} key={`${group.label}:${index}`}>
                  {group.label}
                </th>
              ))}
            </tr>
          ) : null}
          <tr>
            {shownDims.length ? shownDims.map((dim) => <th aria-sort={sortedBy(dim)} className="dim" data-column-key={`dim:${dim.name}`} data-lens-part={`dim:${dim.name}`} key={dim.name}>{heading(dim)}{resizeHandle(`dim:${dim.name}`, dim.label)}</th>) : <th data-column-key="total">Total</th>}
            {slice.flatMap((measure) => [
              <th aria-sort={sortedBy(measure)} className={picked === measure.name ? 'picked' : undefined} data-column-key={`measure:${measure.name}`} data-lens-item={measure.name} key={measure.name} title={measure.definition}>
                {heading(measure)}
                {resizeHandle(`measure:${measure.name}`, measure.label)}
              </th>,
              ...extrasOf(measure).map((style) => (
                <th className="sd" data-column-key={`extra:${measure.name}:${style}`} key={`${measure.name}:${style}`}>
                  {style === 'prior' ? comparison!.label : style === 'pct' ? 'Change %' : 'Change'}
                  {resizeHandle(`extra:${measure.name}:${style}`, `${measure.label} ${style}`)}
                </th>
              )),
            ])}
          </tr>
        </thead>
        <tbody>
          {!dims.length
            ? envelope.rows.slice(0, 1).map((row) => (
                <tr className="total" key="total">
                  <td>{totalLabel}</td>
                  {cells(slice, row, comparison ? envelope.compare[comparison.id]?.[0] : undefined, 'total')}
                </tr>
              ))
            : null}
          {envelope.totals?.current && dims.length ? (
            <tr className="total">
              <td colSpan={shownDims.length || 1}>{totalLabel}</td>
              {cells(slice, envelope.totals.current, comparison ? envelope.totals[comparison.id] : undefined, 'total')}
            </tr>
          ) : null}
          {(dims.length ? visibleRows : []).map((row, rowIndex) => {
            const key = envelope.raw ? `row:${currentPage * ROWS_PER_PAGE + rowIndex}` : keyOf(row);
            const expanded = open.has(key);
            return (
              <React.Fragment key={!envelope.raw && row.other ? `other:${key}` : key}>
                <tr {...rowProps(row)}>
                  <td>
                    {tree ? (
                      <button aria-expanded={expanded} className="tw" onClick={() => setOpen(expanded ? new Set([...open].filter((entry) => entry !== key)) : new Set([...open, key]))} type="button">
                        {expanded ? '▾' : '▸'}
                      </button>
                    ) : null}
                    {member(row, dims[0])}
                  </td>
                  {shownDims.slice(1).map((dim) => (
                    <td className="dim" key={dim.name} title={memberLabel(String(row[dim.name] ?? ''))}>{member(row, dim)}</td>
                  ))}
                  {cells(slice, row, before.get(key), key)}
                </tr>
                {tree && expanded
                  ? childrenOf(row).map((child) => (
                      <tr className="d1" key={child.other ? `other:${keyOf(child)}` : keyOf(child)}>
                        <td>{detailChildLabel(child, dims.map((dim) => dim.name))}</td>
                        {cells(slice, child, before.get(keyOf(child)), keyOf(child))}
                      </tr>
                    ))
                  : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {dims.length && topLevel.length > SHOWN_ROWS ? (
        printing ? (
          <p className="bn">{`First ${SHOWN_ROWS} of ${topLevel.length} rows; Download CSV has them all.`}</p>
        ) : (
          paged ? (
            <div className="pivot-pages">
              <button className="all-rows" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)} type="button">Previous</button>
              <span>{`${currentPage * ROWS_PER_PAGE + 1}–${Math.min((currentPage + 1) * ROWS_PER_PAGE, topLevel.length)} of ${topLevel.length}`}</span>
              <button className="all-rows" disabled={currentPage === lastPage} onClick={() => setPage(currentPage + 1)} type="button">Next</button>
              <button className="all-rows" onClick={() => { setPaged(false); setPage(0); }} type="button">Show fewer</button>
            </div>
          ) : <button className="all-rows" onClick={() => setPaged(true)} type="button">{`Show more rows (${topLevel.length} total)`}</button>
        )
      ) : null}
    </div>
  );
  return <>{slices.map(table)}</>;
}
